import { useCallback, useEffect, useMemo, useState } from 'react';

import AdminPagination from '../admin/AdminPagination';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import FormateurPageShell from './FormateurPageShell';
import { useAdminTablePagination } from '../../hooks/useAdminTablePagination';
import {
  fetchFormateurPrestations,
  fetchFormateurRemuneration,
} from '../../services/academiaFormateurService';
import { formatMoney } from '../../utils/currency';

type PrestationRow = {
  id: string;
  datePrestation: string;
  statut: string;
  heuresPrestees: number;
  montantTotalCdf: number;
};

function formateurDisplayName(user: FormateurRemunerationPanelProps['user']) {
  return [user.profil?.prenom, user.profil?.nom].filter(Boolean).join(' ') || user.telephone || 'Formateur';
}

function prestationStatutLabel(statut: string) {
  const map: Record<string, string> = {
    PLANIFIEE: 'Planifiée',
    REALISEE: 'Réalisée',
    PAYEE: 'Payée',
    ANNULEE: 'Annulée',
  };
  return map[statut] ?? statut;
}

function prestationBadgeClass(statut: string) {
  if (statut === 'PAYEE') return 'formateur-remu-table__badge--paid';
  if (statut === 'PLANIFIEE') return 'formateur-remu-table__badge--pending';
  if (statut === 'ANNULEE') return 'formateur-remu-table__badge--cancelled';
  return 'formateur-remu-table__badge--default';
}

function toNum(v: unknown) {
  if (v == null) return 0;
  return typeof v === 'number' ? v : Number(v) || 0;
}

interface FormateurRemunerationPanelProps {
  user: {
    telephone?: string;
    profil?: { prenom?: string; nom?: string };
  };
}

export default function FormateurRemunerationPanel({ user }: FormateurRemunerationPanelProps) {
  const [synthese, setSynthese] = useState<Record<string, unknown> | null>(null);
  const [prestations, setPrestations] = useState<PrestationRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoadError('');
    setIsLoading(true);
    try {
      const [syn, prest] = await Promise.all([
        fetchFormateurRemuneration(),
        fetchFormateurPrestations(),
      ]);
      setSynthese(syn && typeof syn === 'object' ? syn : null);
      setPrestations(Array.isArray(prest) ? prest : []);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Impossible de charger la rémunération');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const sortedPrestations = useMemo(
    () =>
      [...prestations].sort(
        (a, b) => new Date(b.datePrestation).getTime() - new Date(a.datePrestation).getTime(),
      ),
    [prestations],
  );

  const {
    paginatedItems,
    currentPage,
    totalPages,
    onPageChange,
    summary,
  } = useAdminTablePagination(sortedPrestations);

  const tarif = synthese?.tarifActif as Record<string, number> | undefined;
  const montantDu = toNum(synthese?.montantDuCdf);
  const montantPaye = toNum(synthese?.montantPayeCdf);
  const heuresPrestees = toNum(synthese?.heuresPrestees);
  const joursPrestes = toNum(synthese?.joursPrestes);
  const nombrePrestations = toNum(synthese?.nombrePrestations);
  const montantVacationDu = toNum(synthese?.montantVacationDuCdf);
  const montantTransportDu = toNum(synthese?.montantTransportDuCdf);

  return (
    <FormateurPageShell
      title="Ma rémunération"
      description={`Politique officielle : ${tarif?.referenceUsdHoraire ?? 10} USD/heure de vacation · ${tarif?.referenceUsdTransport ?? 10} USD de transport par jour presté.`}
      isLoading={isLoading}
      isError={Boolean(loadError)}
      errorMessage={loadError}
      onRetry={() => void load()}
      sideCard={{
        label: 'Montant dû',
        value: isLoading ? '…' : formatMoney(montantDu, 'CDF'),
        helper: isLoading
          ? formateurDisplayName(user)
          : `${heuresPrestees} h · ${joursPrestes} jour(s) presté(s)`,
        sideLinks: [
          { label: 'Accueil', to: '/formateur', icon: 'dashboard' },
          { label: 'Mes sessions', to: '/formateur/sessions', icon: 'event' },
        ],
      }}
    >
      <div className="formateur-remu-kpis formateur-remu-kpis--breakdown" aria-label="Ventilation du montant dû">
        <article className="formateur-remu-kpi formateur-remu-kpi--amber">
          <span className="formateur-remu-kpi__icon" aria-hidden>
            <LucideSymbolIcon name="pending" tone="inherit" size={20} />
          </span>
          <div>
            <p className="formateur-remu-kpi__label">Montant dû</p>
            <p className="formateur-remu-kpi__value formateur-remu-kpi__value--sm">
              {isLoading ? '…' : formatMoney(montantDu, 'CDF')}
            </p>
            <p className="formateur-remu-kpi__hint">Total vacation + transport</p>
          </div>
        </article>
        <article className="formateur-remu-kpi formateur-remu-kpi--sky">
          <span className="formateur-remu-kpi__icon" aria-hidden>
            <LucideSymbolIcon name="school" tone="inherit" size={20} />
          </span>
          <div>
            <p className="formateur-remu-kpi__label">Vacation (heures)</p>
            <p className="formateur-remu-kpi__value formateur-remu-kpi__value--sm">
              {isLoading ? '…' : formatMoney(montantVacationDu, 'CDF')}
            </p>
            <p className="formateur-remu-kpi__hint">
              {isLoading ? '…' : `${heuresPrestees} h prestées`}
            </p>
          </div>
        </article>
        <article className="formateur-remu-kpi formateur-remu-kpi--slate">
          <span className="formateur-remu-kpi__icon" aria-hidden>
            <LucideSymbolIcon name="local_shipping" tone="inherit" size={20} />
          </span>
          <div>
            <p className="formateur-remu-kpi__label">Transport</p>
            <p className="formateur-remu-kpi__value formateur-remu-kpi__value--sm">
              {isLoading ? '…' : formatMoney(montantTransportDu, 'CDF')}
            </p>
            <p className="formateur-remu-kpi__hint">
              {isLoading ? '…' : `${joursPrestes} jour(s) presté(s)`}
            </p>
          </div>
        </article>
      </div>

      <div className="formateur-remu-kpis" aria-label="Synthèse rémunération">
        <article className="formateur-remu-kpi formateur-remu-kpi--slate">
          <span className="formateur-remu-kpi__icon" aria-hidden>
            <LucideSymbolIcon name="receipt_long" tone="inherit" size={20} />
          </span>
          <div>
            <p className="formateur-remu-kpi__label">Prestations</p>
            <p className="formateur-remu-kpi__value">{isLoading ? '…' : String(nombrePrestations)}</p>
          </div>
        </article>
        <article className="formateur-remu-kpi formateur-remu-kpi--sky">
          <span className="formateur-remu-kpi__icon" aria-hidden>
            <LucideSymbolIcon name="schedule" tone="inherit" size={20} />
          </span>
          <div>
            <p className="formateur-remu-kpi__label">Heures prestées</p>
            <p className="formateur-remu-kpi__value">{isLoading ? '…' : `${heuresPrestees} h`}</p>
          </div>
        </article>
        <article className="formateur-remu-kpi formateur-remu-kpi--slate">
          <span className="formateur-remu-kpi__icon" aria-hidden>
            <LucideSymbolIcon name="event" tone="inherit" size={20} />
          </span>
          <div>
            <p className="formateur-remu-kpi__label">Jours prestés</p>
            <p className="formateur-remu-kpi__value">{isLoading ? '…' : String(joursPrestes)}</p>
          </div>
        </article>
        <article className="formateur-remu-kpi formateur-remu-kpi--amber">
          <span className="formateur-remu-kpi__icon" aria-hidden>
            <LucideSymbolIcon name="pending" tone="inherit" size={20} />
          </span>
          <div>
            <p className="formateur-remu-kpi__label">Montant dû</p>
            <p className="formateur-remu-kpi__value formateur-remu-kpi__value--sm">
              {isLoading ? '…' : formatMoney(montantDu, 'CDF')}
            </p>
          </div>
        </article>
        <article className="formateur-remu-kpi formateur-remu-kpi--emerald">
          <span className="formateur-remu-kpi__icon" aria-hidden>
            <LucideSymbolIcon name="account_balance_wallet" tone="inherit" size={20} />
          </span>
          <div>
            <p className="formateur-remu-kpi__label">Déjà payé</p>
            <p className="formateur-remu-kpi__value formateur-remu-kpi__value--sm">
              {isLoading ? '…' : formatMoney(montantPaye, 'CDF')}
            </p>
          </div>
        </article>
      </div>

      {tarif ? (
        <p className="formateur-remu-policy">
          <LucideSymbolIcon name="info" tone="inherit" size={16} />
          <span>
            Taux appliqué : vacation {tarif.referenceUsdHoraire ?? 10} USD/h
            {tarif.referenceUsdTransport != null
              ? ` · transport ${tarif.referenceUsdTransport} USD/jour`
              : ''}
            {tarif.tauxChangeUsdCdf
              ? ` · taux ${Number(tarif.tauxChangeUsdCdf).toLocaleString('fr-FR')} CDF/USD`
              : ''}
          </span>
        </p>
      ) : null}

      <section className="formateur-remu-table-card" aria-labelledby="formateur-remu-prestations-heading">
        <header className="formateur-remu-table-card__header">
          <span className="formateur-remu-table-card__header-icon" aria-hidden>
            <LucideSymbolIcon name="receipt_long" tone="inherit" size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="formateur-remu-prestations-heading" className="formateur-remu-table-card__title">
              Mes prestations
            </h2>
            <p className="formateur-remu-table-card__subtitle">
              {isLoading
                ? 'Chargement…'
                : prestations.length === 0
                  ? 'Historique vide pour le moment.'
                  : `${prestations.length} prestation${prestations.length > 1 ? 's' : ''} enregistrée${prestations.length > 1 ? 's' : ''}`}
            </p>
          </div>
        </header>

        {prestations.length === 0 && !isLoading ? (
          <div className="formateur-remu-table-card__empty">
            <LucideSymbolIcon name="receipt_long" tone="inherit" size={32} />
            <p>Aucune prestation enregistrée pour le moment.</p>
          </div>
        ) : (
          <>
            <div className="formateur-remu-table-wrap">
              <table className="formateur-remu-table">
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col" className="formateur-remu-table__num">
                      Heures
                    </th>
                    <th scope="col" className="formateur-remu-table__num">
                      Montant
                    </th>
                    <th scope="col">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedItems.map((p) => (
                    <tr key={p.id}>
                      <td data-label="Date">
                        {new Date(p.datePrestation).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </td>
                      <td className="formateur-remu-table__num" data-label="Heures">
                        {p.heuresPrestees} h
                      </td>
                      <td className="formateur-remu-table__num formateur-remu-table__amount" data-label="Montant">
                        {formatMoney(toNum(p.montantTotalCdf), 'CDF')}
                      </td>
                      <td data-label="Statut">
                        <span
                          className={`formateur-remu-table__badge ${prestationBadgeClass(p.statut)}`}
                        >
                          {prestationStatutLabel(p.statut)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {prestations.length > 0 ? (
              <footer className="formateur-remu-table-card__footer">
                <AdminPagination
                  className="formateur-remu-pagination"
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                  summary={summary}
                />
              </footer>
            ) : null}
          </>
        )}
      </section>
    </FormateurPageShell>
  );
}
